/**
 * Changing a number by hand, and what the Act says about it.
 *
 * Shifty warns, it does not enforce. Everything here returns warnings for a
 * screen to show beside the figure; nothing here refuses anything. An employer
 * has reasons Shifty cannot see, and a register that declines to record what
 * happened is tidy and false.
 *
 * SOUTH AFRICA ONLY — BCEA ss 33 and 34. See the future-markets note.
 */

import type { PayAdjustment } from "./types";

export type AdjustmentDirection = "deduction" | "addition";

/**
 * Why a deduction is lawful, per BCEA s34(1):
 *
 *   An employer may not make any deduction from an employee's remuneration
 *   unless — (a) subject to subsection (2), the employee in writing agrees to
 *   the deduction in respect of a debt specified in the agreement; or (b) the
 *   deduction is required or permitted in terms of a law, collective
 *   agreement, court order or arbitration award.
 *
 * UIF is `required_by_law`. An advance the worker signed for is
 * `agreed_in_writing`. `loss_or_damage` is the one s34(2) puts conditions on.
 */
export type AdjustmentBasis =
  | "agreed_in_writing"
  | "required_by_law"
  | "court_order"
  | "loss_or_damage"
  | "other";

export const BASIS_LABEL: Record<AdjustmentBasis, string> = {
  agreed_in_writing: "They agreed in writing",
  required_by_law: "Required by law",
  court_order: "Court order or arbitration award",
  loss_or_damage: "Loss or damage they caused",
  other: "Something else",
};

export interface AdjustmentWarning {
  /** Short enough to sit under a field. */
  text: string;
  /** Which part of the Act it comes from, for anybody who wants to check. */
  section: string;
}

/**
 * What the Act would say about this deduction, as warnings rather than refusals.
 *
 * `remunerationInMoney` is what the period pays before deductions — the Act's
 * own phrase in s34(2)(d) — and is used only for the quarter cap.
 */
export function adjustmentWarnings(
  adj: Pick<PayAdjustment, "direction" | "basis" | "amount">,
  context: { remunerationInMoney: number; lossOrDamageTotal: number },
): AdjustmentWarning[] {
  const out: AdjustmentWarning[] = [];
  if (adj.direction !== "deduction") return out;

  if (!adj.basis) {
    out.push({
      section: "BCEA s34(1)",
      text:
        "A deduction needs a reason the Act allows: they agreed in writing to a " +
        "specified debt, or a law, court order or arbitration award requires it.",
    });
  }

  if (adj.basis === "loss_or_damage") {
    out.push({
      section: "BCEA s34(2)",
      text:
        "Deducting for loss or damage is only allowed if it happened at work and " +
        "was their fault, you followed a fair procedure and gave them a chance to " +
        "respond, and you take no more than the loss actually cost.",
    });

    // s34(2)(d): "the total deductions from the employee's remuneration IN
    // TERMS OF THIS SUBSECTION do not exceed one-quarter". The cap is on
    // loss-and-damage deductions, not on deductions in general, and saying
    // otherwise would be overstating the Act.
    const cap = context.remunerationInMoney / 4;
    if (context.lossOrDamageTotal > cap) {
      out.push({
        section: "BCEA s34(2)(d)",
        text:
          "Loss and damage deductions cannot come to more than a quarter of what " +
          "they are paid in money for the period.",
      });
    }
  }

  return out;
}

/** What the period comes to once the adjustments are applied. */
export interface AdjustedTotal {
  /** Before anything was added or taken away. */
  gross: number;
  additions: number;
  deductions: number;
  /** What is actually paid. */
  net: number;
}

export function applyAdjustments(gross: number, adjustments: PayAdjustment[]): AdjustedTotal {
  let additions = 0;
  let deductions = 0;
  for (const a of adjustments) {
    if (a.direction === "addition") additions += a.amount;
    else deductions += a.amount;
  }
  return { gross, additions, deductions, net: gross + additions - deductions };
}

/** Loss-and-damage deductions only, which is what s34(2)(d) caps. */
export const lossOrDamageTotal = (adjustments: PayAdjustment[]) =>
  adjustments
    .filter((a) => a.direction === "deduction" && a.basis === "loss_or_damage")
    .reduce((sum, a) => sum + a.amount, 0);

/**
 * Warnings about the period as a whole, rather than about one entry.
 *
 * Net pay below zero is not unlawful in itself — an employee can owe more than
 * a month pays — but it is almost always a typo, and it is the kind of typo
 * that reaches somebody's bank account.
 */
export function periodWarnings(total: AdjustedTotal): AdjustmentWarning[] {
  const out: AdjustmentWarning[] = [];
  if (total.net < 0) {
    out.push({
      section: "Check this",
      text: "The deductions come to more than the period pays, so this would pay nothing and still leave a balance owing.",
    });
  }
  if (total.gross > 0 && total.deductions > total.gross / 2) {
    out.push({
      section: "Check this",
      text: "More than half of what they earned is being deducted.",
    });
  }
  return out;
}
